import { useCallback, useEffect, useRef, useState } from "react";
import { useCommandeLock } from "../../../hooks/useCommandeLock";
import { registerSave, unregisterSave } from "../../save/pendingSave";
import { clearDraftBackup, saveDraftBackup } from "../../save/draftBackup";
import { AUTOSAVE_DELAY_MS } from "../model/commandeConstants";
import { buildCommandeListPatch, cloneDraft, draftToListPatch, ensureDraftClientKeys, fromBackendToDraft } from "../model/commandeDraft";
import { mergeSavedDraft } from "../model/commandeDraftMerge";
import { hasIncompleteMedicamentDestination, hasIncompletePatientIdentity, sameUser, validatePatients } from "../model/commandeValidation";
import { resolveIdentityConflict, runSave as runCommandeSave } from "../save/commandeSaveEngine";

// Verrou de la commande ouverte. Un verrou détenu par l'utilisateur courant dans un autre onglet compte comme le sien.
export function useCommandeLockState(selectedCommande, user) {
    const currentUserEmail = user?.email || user?.username || "";
    const lock = useCommandeLock(selectedCommande?.id);
    const effectiveHasLock = lock.hasLock || sameUser(lock.lockedBy, currentUserEmail);
    const hasLockRef = useRef(effectiveHasLock);
    hasLockRef.current = effectiveHasLock;
    const selectedCommandeRef = useRef(selectedCommande);
    selectedCommandeRef.current = selectedCommande;
    return { ...lock, effectiveHasLock, hasLockRef, selectedCommandeRef };
}

// Brouillon de la commande ouverte et sa référence serveur. Les refs sont mises à jour dans le même tour que la
// saisie : un clic sur « Sauvegarder » juste après une frappe doit lire le brouillon à jour.
export function useCommandeDraft({ context, list }) {
    const [commandeDetail, setCommandeDetail] = useState(null);
    const [draft, setDraft] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
    const [saveErrors, setSaveErrors] = useState([]);
    const [patientIdentityConflict, setPatientIdentityConflict] = useState(null);
    const isDirtyRef = useRef(isDirty);
    const draftRef = useRef(draft);
    const refs = {
        savingRef: useRef(false),
        saveAgainRef: useRef(false),
        currentSavePromiseRef: useRef(null),
        patientIdentityConflictRef: useRef(null),
        autoSaveStatusResetRef: useRef(null),
        autoSaveTimerRef: useRef(null),
        draftRevisionRef: useRef(0),
        restoredBackupRef: useRef(false),
        // Draft de référence = dernier état reçu du serveur (pour le diff de patches)
        originalDraftRef: useRef(null),
    };
    isDirtyRef.current = isDirty;
    draftRef.current = draft;
    const defaultMedicamentDestination = context?.medicamentStrategy?.defaultDestination || "ROBOT";
    const { setCommandes, applyListPatches, optimisticListPatchesRef } = list;
    const { draftRevisionRef, restoredBackupRef, originalDraftRef, autoSaveStatusResetRef, autoSaveTimerRef } = refs;

    const rememberDraftForList = useCallback((draftToRemember = draftRef.current) => {
        const patch = draftToListPatch(draftToRemember, defaultMedicamentDestination);
        if (!draftToRemember?.id || !patch) return;

        optimisticListPatchesRef.current.set(draftToRemember.id, patch);
        setCommandes(prev => applyListPatches(prev.map(cmd =>
            cmd.id === draftToRemember.id ? { ...cmd, ...patch } : cmd
        )));
    }, [applyListPatches, defaultMedicamentDestination, optimisticListPatchesRef, setCommandes]);

    const updateDraft = useCallback((updater) => {
        restoredBackupRef.current = false;
        draftRevisionRef.current += 1;
        isDirtyRef.current = true;
        setIsDirty(true);
        setSaveErrors([]);
        setAutoSaveStatus("local");
        const previous = draftRef.current;
        const next = ensureDraftClientKeys(
            typeof updater === "function" ? updater(previous) : updater
        );
        draftRef.current = next;
        setDraft(next);
    }, [draftRevisionRef, restoredBackupRef]);

    // Met à jour uniquement l'état de workflow renvoyé par le serveur, sans rendre le brouillon sale.
    const setWorkflowDraft = useCallback((updater) => {
        const previous = draftRef.current;
        const next = ensureDraftClientKeys(
            typeof updater === "function" ? updater(previous) : updater
        );
        draftRef.current = next;
        setDraft(next);
    }, []);

    const syncSavedCommande = useCallback((savedCommande, sentDraft, currentDraftOverride = null) => {
        if (!savedCommande?.id) return;
        const savedDraft = fromBackendToDraft(savedCommande);
        // La référence de comparaison doit représenter uniquement ce que le
        // serveur vient réellement de confirmer. Le brouillon fusionné peut
        // contenir un nom ou une date saisis pendant la requête : le considérer
        // comme persisté empêcherait la sauvegarde de rattrapage de les envoyer.
        const persistedDraft = cloneDraft(savedDraft);
        const mergedDraft = ensureDraftClientKeys(
            mergeSavedDraft(currentDraftOverride || draftRef.current, sentDraft, savedDraft)
        );
        const patch = {
            ...buildCommandeListPatch(savedCommande),
            ...draftToListPatch(mergedDraft, defaultMedicamentDestination),
        };
        setCommandes(prev => prev.map(cmd =>
            cmd.id === savedCommande.id ? { ...cmd, ...patch } : cmd
        ));
        // Ne pas toucher selectedCommande : seul son id est utilisé, et créer
        // un nouvel objet déclencherait le useEffect qui re-fetch et écraserait
        // les changements locaux non encore confirmés (ex. EHPAD).
        setCommandeDetail(prev =>
            prev?.id === savedCommande.id ? { ...prev, ...savedCommande } : prev
        );
        draftRef.current = mergedDraft;
        setDraft(mergedDraft);
        originalDraftRef.current = persistedDraft;
        optimisticListPatchesRef.current.delete(savedCommande.id);
        clearDraftBackup(savedCommande.id);
    }, [defaultMedicamentDestination, optimisticListPatchesRef, originalDraftRef, setCommandes]);

    const resetSavedStatusLater = useCallback(() => {
        if (autoSaveStatusResetRef.current) clearTimeout(autoSaveStatusResetRef.current);
        autoSaveStatusResetRef.current = setTimeout(() => {
            setAutoSaveStatus(prev => prev === "saved" ? "idle" : prev);
            autoSaveStatusResetRef.current = null;
        }, 3000);
    }, [autoSaveStatusResetRef]);

    // Fermeture du détail : plus de brouillon ni de modification en attente.
    const clearDraft = useCallback(() => {
        setCommandeDetail(null);
        setDraft(null);
        draftRef.current = null;
        isDirtyRef.current = false;
        restoredBackupRef.current = false;
        setIsDirty(false);
    }, [restoredBackupRef]);

    useEffect(() => () => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        if (autoSaveStatusResetRef.current) clearTimeout(autoSaveStatusResetRef.current);
    }, [autoSaveStatusResetRef, autoSaveTimerRef]);

    return {
        ...refs, commandeDetail, setCommandeDetail, draft, setDraft, isDirty, setIsDirty, autoSaveStatus, setAutoSaveStatus,
        saveErrors, setSaveErrors, patientIdentityConflict, setPatientIdentityConflict, isDirtyRef, draftRef,
        defaultMedicamentDestination, rememberDraftForList, updateDraft, setWorkflowDraft, syncSavedCommande,
        resetSavedStatusLater, clearDraft,
    };
}

// Sauvegardes de la commande ouverte : manuelle, automatique après une pause d'édition, et à la sortie de la page.
export function useCommandeSaving({ d, list, lock, view }) {
    const runSaveRef = useRef(null);
    const saveContextRef = useRef(null);
    saveContextRef.current = {
        ...d, optimisticListPatchesRef: list.optimisticListPatchesRef, hasLockRef: lock.hasLockRef,
        tryAcquire: lock.tryAcquire, runSaveRef,
    };
    // Stable : le contexte est relu à chaque appel.
    const runSave = useCallback(options => runCommandeSave(saveContextRef.current, options), []);
    runSaveRef.current = runSave;

    const resolvePatientIdentityConflict = useCallback((resolution) => resolveIdentityConflict(saveContextRef.current, (patient, conflict) => ({
        ...patient,
        _identityResolution: resolution,
        _identityCandidateId: conflict.existingPatientId,
    })), []);

    const cancelPatientIdentityConflict = useCallback(() => resolveIdentityConflict(saveContextRef.current, (patient, conflict) => ({
        ...patient,
        id: conflict.existingPatientId ?? patient.id ?? null,
        dateNaissance: conflict.existingDateNaissance ?? null,
        _identityResolution: undefined,
        _identityCandidateId: undefined,
    }), { clearErrors: true }), []);

    const saveDraft = useCallback(async () => runSave({ validate: false, silent: false }), [runSave]);
    const save = useCallback(async () => runSave({ validate: false, silent: false, force: true }), [runSave]);

    const commitDraft = async () => {
        if (!d.draft?.id) return false;
        if (!d.isDirty) return true;
        const errors = validatePatients(d.draft.patients || []);
        if (errors.length > 0) {
            alert("Corrige les champs avant de continuer");
            return false;
        }
        return runSave({ validate: false, silent: false });
    };

    useAutoSave({ d, effectiveHasLock: lock.effectiveHasLock, runSave });

    useEffect(() => {
        if (view === "detail") {
            registerSave(saveDraft);
        } else {
            unregisterSave();
        }
        return () => unregisterSave();
    }, [view, saveDraft]);

    return { runSave, saveDraft, save, commitDraft, resolvePatientIdentityConflict, cancelPatientIdentityConflict };
}

// Sauvegarde automatique 2,5 s après la dernière saisie, seulement si l'utilisateur tient le verrou. Un brouillon
// récupéré du navigateur reste visible et peut être sauvegardé manuellement, mais n'est jamais réinjecté tout seul
// dans la base : une modification utilisateur réactive l'autosauvegarde.
function useAutoSave({ d, effectiveHasLock, runSave }) {
    const { draft, isDirty, restoredBackupRef, autoSaveTimerRef, setAutoSaveStatus, defaultMedicamentDestination } = d;
    useEffect(() => {
        if (!draft?.id || !isDirty || !effectiveHasLock) return;
        if (restoredBackupRef.current) {
            setAutoSaveStatus("local");
            return;
        }
        saveDraftBackup(draft.id, draft);
        setAutoSaveStatus(prev => ["saving", "partial", "error"].includes(prev) ? prev : "local");

        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        if (hasIncompletePatientIdentity(draft.patients)
            || hasIncompleteMedicamentDestination(draft.patients)) return;
        autoSaveTimerRef.current = setTimeout(() => {
            autoSaveTimerRef.current = null;
            runSave({ validate: true, silent: true });
        }, AUTOSAVE_DELAY_MS);

        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
                autoSaveTimerRef.current = null;
            }
        };
    }, [defaultMedicamentDestination, draft, effectiveHasLock, isDirty, runSave, restoredBackupRef, autoSaveTimerRef, setAutoSaveStatus]);
}
